import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { purchaseApi } from '../../api';
import {
  PageHeader,
  LoadingState,
  ErrorState,
  EmptyState,
} from '../../components/common';
import { getErrorMessage } from '../../api/client';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

import { Modal } from '../../components/Modal';

// ============================================================
// SupplierScorecardPage — FR-PUR-02.05
// ============================================================
export function SupplierScorecardPage() {
  const [supplierId, setSupplierId] = useState<string>('');
  const supQuery = useQuery({ queryKey: ['suppliers'], queryFn: () => purchaseApi.listSuppliers() });
  const scoreQuery = useQuery({
    queryKey: ['supplier-scorecard', supplierId],
    queryFn: () => purchaseApi.getSupplierScorecard(supplierId),
    enabled: !!supplierId,
  });

  const selectedSupplier = supQuery.data?.find((s: any) => s.id === supplierId);

  return (
    <div className="px-8 py-10 max-w-7xl mx-auto">
      <PageHeader
        eyebrow="Purchase · Performance"
        title="Supplier scorecards"
        subtitle="On-time delivery, quality, RTV rate, and spend over a trailing 12-month window."
      />

      {/* Supplier picker */}
      <div className="card p-5 mb-6">
        <div className="eyebrow text-ink-500 mb-2">Supplier</div>
        {selectedSupplier ? (
          <div className="flex gap-3 items-center bg-canvas-subtle rounded-xl px-3 py-2.5">
            <span className="font-mono text-xs font-semibold text-ink-900">{selectedSupplier.code}</span>
            <span className="flex-1 font-semibold text-ink-900 truncate">{selectedSupplier.legal_name}</span>
            <button
              className="text-xs font-medium text-ink-500 hover:text-ink-900 transition-colors"
              onClick={() => setSupplierId('')}
            >
              Change
            </button>
          </div>
        ) : (
          <select
            className="input max-w-md"
            value={supplierId}
            onChange={(e) => setSupplierId(e.target.value)}
          >
            <option value="">Select a supplier…</option>
            {supQuery.data?.map((s: any) => (
              <option key={s.id} value={s.id}>{s.code} — {s.legal_name}</option>
            ))}
          </select>
        )}
      </div>

      {!supplierId ? (
        <div className="card">
          <EmptyState
            message="Select a supplier"
            hint="Pick a supplier above to view their performance metrics and composite grade."
          />
        </div>
      ) : scoreQuery.isLoading ? (
        <div className="card"><LoadingState rows={4} /></div>
      ) : scoreQuery.error ? (
        <ErrorState error={scoreQuery.error} />
      ) : (
        <ScorecardBody data={scoreQuery.data} />
      )}
    </div>
  );
}

function ScorecardBody({ data }: { data: any }) {
  const s = data.score;
  const m = data.metrics;

  const gradeTone =
    s.grade === 'A' ? 'bg-sage-50 text-sage-700 ring-sage-200' :
    s.grade === 'B' ? 'bg-blue-50 text-blue-700 ring-blue-200' :
    s.grade === 'C' ? 'bg-amber-50 text-amber-800 ring-amber-200' :
    s.grade === 'D' ? 'bg-red-50 text-red-700 ring-red-200' :
                      'bg-canvas-subtle text-ink-700 ring-ink-200';

  return (
    <div className="space-y-6">
      {/* Composite score hero */}
      <div className="card p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-center gap-6">
            <div>
              <div className="eyebrow text-ink-500 mb-1">Composite score</div>
              <div className="flex items-baseline gap-3">
                <div className="font-display font-bold text-5xl text-ink-900 tabular-nums tracking-tight">
                  {s.composite ?? '—'}
                </div>
                {s.grade && (
                  <span className={clsx(
                    'inline-flex items-center font-display font-bold text-2xl uppercase px-3 py-1 rounded-lg ring-1 ring-inset',
                    gradeTone
                  )}>
                    {s.grade}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="text-right text-2xs text-ink-500">
            <div className="eyebrow mb-1">Weights</div>
            <div className="font-mono tabular-nums">
              OTD {(s.weighting.otd * 100).toFixed(0)}% · Quality {(s.weighting.quality * 100).toFixed(0)}% · RTV {(s.weighting.rtv * 100).toFixed(0)}%
            </div>
          </div>
        </div>
      </div>

      {/* Primary metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricCard
          label="On-time delivery"
          value={m.onTimePct != null ? `${m.onTimePct}%` : '—'}
          sublabel={`${m.onTimeGrns} of ${m.confirmedGrns} GRNs on time`}
          accent={m.onTimePct != null ? (m.onTimePct >= 90 ? 'sage' : m.onTimePct >= 75 ? 'amber' : 'danger') : 'default'}
        />
        <MetricCard
          label="Rejection rate"
          value={m.rejectionPct != null ? `${m.rejectionPct}%` : '—'}
          sublabel={`${m.totalRejectedQty.toFixed(0)} of ${(m.totalReceivedQty + m.totalRejectedQty).toFixed(0)} units`}
          accent={m.rejectionPct != null ? (m.rejectionPct <= 2 ? 'sage' : m.rejectionPct <= 5 ? 'amber' : 'danger') : 'default'}
        />
        <MetricCard
          label="Avg lead time"
          value={m.avgLeadDays != null ? `${m.avgLeadDays}d` : '—'}
          sublabel="Order → receipt"
        />
      </div>

      {/* Volume metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricCard
          label="Total spend (ordered)"
          value={`${fmtCurrency(m.totalPoValue)}`}
          sublabel={`${m.totalPos} PO${m.totalPos === 1 ? '' : 's'} · ${m.openPos} still open`}
        />
        <MetricCard
          label="Confirmed received value"
          value={`${fmtCurrency(m.confirmedGrnValue)}`}
          sublabel={`${m.confirmedGrns} confirmed GRN${m.confirmedGrns === 1 ? '' : 's'}`}
        />
        <MetricCard
          label="RTVs"
          value={m.totalRtvs}
          sublabel={`${fmtCurrency(m.totalRtvValue)} returned · ${m.rtvRate ?? '—'}% of receipts`}
          accent={m.totalRtvs > 0 ? 'amber' : 'default'}
        />
      </div>

      <div className="text-2xs text-ink-500 text-center font-mono tabular-nums">
        Period · {new Date(data.period.from).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
        <span className="mx-2 text-ink-400">→</span>
        {new Date(data.period.to).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
      </div>
    </div>
  );
}

function MetricCard({
  label, value, sublabel, accent = 'default',
}: {
  label: string;
  value: number | string;
  sublabel?: string;
  accent?: 'default' | 'sage' | 'amber' | 'danger';
}) {
  const bar = {
    default: 'bg-ink-200',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  const valueTone = {
    default: 'text-ink-900',
    sage:    'text-sage-700',
    amber:   'text-amber-700',
    danger:  'text-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-ink-500">{label}</div>
      <div className={clsx(
        'mt-3 text-2xl font-bold font-display tabular-nums',
        valueTone
      )}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      {sublabel && (
        <div className="text-2xs text-ink-500 mt-1">{sublabel}</div>
      )}
    </div>
  );
}

// ============================================================
// SupplierOnboardingPage — FR-PUR-02.06
// ============================================================
export function SupplierOnboardingPage() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<any | null>(null);
  const [showAdvance, setShowAdvance] = useState(false);

  const listQuery = useQuery({
    queryKey: ['suppliers', 'pending-onboarding'],
    queryFn: () => purchaseApi.listPendingOnboarding(),
  });

  const kpis = (() => {
    const rows = listQuery.data ?? [];
    let registration = 0, docs = 0, verified = 0, rejected = 0;
    for (const s of rows) {
      if (s.onboarding_stage === 'registration') registration++;
      else if (s.onboarding_stage === 'docs_submitted') docs++;
      else if (s.onboarding_stage === 'verified') verified++;
      else if (s.onboarding_stage === 'rejected') rejected++;
    }
    return { total: rows.length, registration, docs, verified, rejected };
  })();

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader
        eyebrow="Purchase · Lifecycle"
        title="Supplier onboarding"
        subtitle="Progression through registration, document submission, verification, and final approval."
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="In pipeline" value={kpis.total} />
        <KpiTile label="Awaiting docs" value={kpis.registration + kpis.docs}
          accent={(kpis.registration + kpis.docs) > 0 ? 'amber' : 'default'} />
        <KpiTile label="Ready to approve" value={kpis.verified}
          accent={kpis.verified > 0 ? 'sage' : 'default'} />
        <KpiTile label="Rejected" value={kpis.rejected}
          accent={kpis.rejected > 0 ? 'danger' : 'default'} />
      </div>

      {listQuery.isLoading ? (
        <div className="card"><LoadingState rows={4} /></div>
      ) : listQuery.error ? (
        <ErrorState error={listQuery.error} />
      ) : !listQuery.data?.length ? (
        <div className="card">
          <EmptyState
            message="No pending onboardings"
            hint="All current suppliers are approved. New supplier applications will appear here."
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_34rem] gap-6">
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Stage</th>
                    <th>Started</th>
                  </tr>
                </thead>
                <tbody>
                  {(listQuery.data ?? []).map((s: any) => (
                    <tr key={s.id}
                        onClick={() => setSelected(s)}
                        className={`cursor-pointer ${selected?.id === s.id ? '!bg-canvas-subtle' : ''}`}>
                      <td className="font-mono text-xs font-semibold text-ink-900">{s.code}</td>
                      <td className="text-sm text-ink-900">{s.legal_name}</td>
                      <td><OnboardingStageBadge stage={s.onboarding_stage} /></td>
                      <td className="text-xs text-ink-500 font-mono tabular-nums">
                        {s.onboarding_started_at
                          ? new Date(s.onboarding_started_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                          : <span className="text-ink-400">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card p-5 min-h-[400px]">
            {!selected ? (
              <EmptyState
                message="Pick a supplier"
                hint="Click a row on the left to view stage progression and advance their onboarding."
              />
            ) : (
              <OnboardingDetail
                supplier={selected}
                onAdvance={() => setShowAdvance(true)}
              />
            )}
          </div>
        </div>
      )}

      {showAdvance && selected && (
        <AdvanceStageModal
          supplier={selected}
          onClose={() => setShowAdvance(false)}
          onAdvanced={() => {
            setShowAdvance(false);
            qc.invalidateQueries({ queryKey: ['suppliers', 'pending-onboarding'] });
            setSelected(null);
          }}
        />
      )}
    </div>
  );
}

function OnboardingStageBadge({ stage }: { stage: string }) {
  const cls = {
    registration:   'bg-canvas-subtle text-ink-700 ring-ink-200',
    docs_submitted: 'bg-amber-50 text-amber-800 ring-amber-200',
    verified:       'bg-blue-50 text-blue-700 ring-blue-200',
    approved:       'bg-sage-50 text-sage-700 ring-sage-200',
    rejected:       'bg-red-50 text-red-700 ring-red-200',
  }[stage] ?? 'bg-canvas-subtle text-ink-700 ring-ink-200';
  return (
    <span className={clsx(
      'inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset',
      cls
    )}>
      {stage.replace('_', ' ')}
    </span>
  );
}

function OnboardingDetail({ supplier, onAdvance }: { supplier: any; onAdvance: () => void }) {
  const stages: Array<{ key: string; label: string }> = [
    { key: 'registration',   label: 'Registration' },
    { key: 'docs_submitted', label: 'Docs submitted' },
    { key: 'verified',       label: 'Verified' },
    { key: 'approved',       label: 'Approved' },
  ];
  const currentIdx = stages.findIndex((s) => s.key === supplier.onboarding_stage);
  const rejected = supplier.onboarding_stage === 'rejected';

  return (
    <div>
      {/* Supplier header */}
      <div className="mb-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="eyebrow text-ink-500 mb-1">Supplier</div>
            <div className="font-mono text-xs text-ink-500">{supplier.code}</div>
            <div className="font-display font-bold text-lg text-ink-900">{supplier.legal_name}</div>
            {supplier.email && (
              <div className="text-xs text-ink-600 mt-0.5 font-mono">{supplier.email}</div>
            )}
          </div>
          <OnboardingStageBadge stage={supplier.onboarding_stage} />
        </div>
      </div>

      {/* Stepper */}
      {!rejected && (
        <div className="mb-5">
          <div className="eyebrow text-ink-500 mb-3">Progress</div>
          <div className="flex items-start">
            {stages.map((stage, i) => {
              const isComplete = i < currentIdx;
              const isCurrent  = i === currentIdx;
              const isFuture   = i > currentIdx;
              return (
                <div key={stage.key} className={clsx('flex-1 flex flex-col items-center relative', i > 0 && 'before:content-[""] before:absolute before:h-0.5 before:top-4 before:-left-1/2 before:w-full', isComplete || (isCurrent && i > 0) ? 'before:bg-sage-500' : 'before:bg-ink-200')}>
                  <div className={clsx(
                    'w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ring-2 ring-canvas-elevated relative z-10',
                    isComplete ? 'bg-sage-500 text-white' :
                    isCurrent  ? 'bg-amber-400 text-white' :
                                 'bg-canvas-subtle text-ink-400'
                  )}>
                    {isComplete ? '✓' : i + 1}
                  </div>
                  <div className={clsx(
                    'mt-2 text-center text-2xs font-semibold uppercase tracking-wider max-w-[6rem]',
                    isFuture ? 'text-ink-400' : 'text-ink-700'
                  )}>
                    {stage.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Notes / rejection / verified metadata */}
      {supplier.onboarding_notes && (
        <div className="bg-canvas-subtle rounded-xl p-3 mb-3 text-xs">
          <div className="eyebrow text-ink-500 mb-1">Notes</div>
          <div className="text-ink-700 whitespace-pre-line">{supplier.onboarding_notes}</div>
        </div>
      )}
      {supplier.rejection_reason && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-3 text-xs">
          <div className="eyebrow text-danger mb-1">Rejected</div>
          <div className="text-red-800">{supplier.rejection_reason}</div>
        </div>
      )}
      {supplier.verified_at && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-3 text-xs text-blue-800">
          <div className="eyebrow text-blue-700 mb-1">Verified</div>
          <div>
            {new Date(supplier.verified_at).toLocaleString()}
            {supplier.verified_by_name && <> · {supplier.verified_by_name}</>}
          </div>
        </div>
      )}

      {supplier.onboarding_stage !== 'approved' && supplier.onboarding_stage !== 'rejected' && (
        <button className="btn-primary w-full mt-2" onClick={onAdvance}>
          Advance stage
        </button>
      )}
    </div>
  );
}

function AdvanceStageModal({ supplier, onClose, onAdvanced }:
    { supplier: any; onClose: () => void; onAdvanced: () => void }) {
  const stages = ['registration', 'docs_submitted', 'verified', 'approved'];
  const currentIdx = stages.indexOf(supplier.onboarding_stage);
  const nextStage = currentIdx < stages.length - 1 ? stages[currentIdx + 1] : null;

  const [toStage, setToStage] = useState<string>(nextStage ?? 'approved');
  const [notes, setNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  const mut = useMutation({
    mutationFn: () => purchaseApi.advanceOnboarding(supplier.id, {
      toStage: toStage as any,
      notes: notes || undefined,
      rejectionReason: toStage === 'rejected' ? (rejectionReason || 'Not specified') : undefined,
    }),
    onSuccess: (d) => { toast.success(`Advanced: ${d.from} → ${d.to}`); onAdvanced(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className={clsx(
          'eyebrow mb-1',
          toStage === 'rejected' ? 'text-danger' : 'text-sage-700'
        )}>
          Advance · Onboarding
        </div>
        <h2 className="text-xl font-bold font-display text-ink-900">
          {toStage === 'rejected' ? 'Reject supplier' : 'Advance stage'}
        </h2>
        <div className="mt-2 flex items-center gap-2 text-sm">
          <span className="text-ink-700">{supplier.legal_name}</span>
          <span className="text-ink-400">·</span>
          <OnboardingStageBadge stage={supplier.onboarding_stage} />
        </div>
      </div>

      <div className="px-6 py-5 space-y-4">
        <div>
          <label className="label">Move to stage</label>
          <select className="input" value={toStage} onChange={(e) => setToStage(e.target.value)}>
            {nextStage && (
              <option value={nextStage}>
                {nextStage.replace('_', ' ')} (next)
              </option>
            )}
            <option value="rejected">rejected</option>
          </select>
        </div>

        <div>
          <label className="label">Notes <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <textarea
            className="input"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Context for the next reviewer"
          />
        </div>

        {toStage === 'rejected' && (
          <div>
            <label className="label">Rejection reason <span className="text-danger">*</span></label>
            <input
              className="input"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="e.g. Missing tax documents"
            />
          </div>
        )}
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button
          className={clsx(
            'btn-primary',
            toStage === 'rejected' && '!bg-danger hover:!bg-red-700'
          )}
          disabled={mut.isPending || (toStage === 'rejected' && !rejectionReason)}
          onClick={() => mut.mutate()}
        >
          {mut.isPending ? 'Saving…' : toStage === 'rejected' ? 'Reject' : 'Advance'}
        </button>
      </div>
    </Modal>
  );
}

// ============================================================
// Shared
// ============================================================
function KpiTile({
  label, value, accent = 'default',
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'sage' | 'amber' | 'danger';
}) {
  const bar = {
    default: 'bg-ink-200',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-ink-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-ink-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}
